/**
 * PatchList - fallback patches uploaded when publish (push/PR creation)
 * failed after a fix was already committed and reviewed locally
 */

import { useState } from 'react';
import { FileDiff, Download } from 'lucide-react';
import { Card } from '../common';
import { tasksApi } from '../../lib/tasks-api';
import type { TaskPatch } from '../../lib/tasks-api';

interface PatchListProps {
  taskId: string;
  patches: TaskPatch[];
}

export function PatchList({ taskId, patches }: PatchListProps) {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  if (patches.length === 0) {
    return null;
  }

  const handleDownload = async (patchId: string) => {
    setError(null);
    setDownloadingId(patchId);
    try {
      const { url } = await tasksApi.getPatchDownloadUrl(taskId, patchId);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      console.error('Failed to download patch:', err);
      setError('Failed to download patch');
    } finally {
      setDownloadingId(null);
    }
  };

  return (
    <Card>
      <div className="flex items-center gap-3 mb-4">
        <FileDiff className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          Fallback Patches
        </h3>
      </div>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Publishing failed for these fixes after they were committed and reviewed. Download the
        patch and apply it manually.
      </p>
      {error && <p className="text-sm text-red-600 dark:text-red-400 mb-4">{error}</p>}
      <div className="space-y-3">
        {patches.map((patch) => (
          <div
            key={patch.id}
            className="flex items-center justify-between p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg"
          >
            <div>
              <p className="text-sm font-semibold text-gray-900 dark:text-white">
                {patch.repoFullName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                Branch: {patch.branchName}
              </p>
            </div>
            <button
              onClick={() => handleDownload(patch.id)}
              disabled={downloadingId === patch.id}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              {downloadingId === patch.id ? 'Downloading...' : 'Download'}
            </button>
          </div>
        ))}
      </div>
    </Card>
  );
}
